/**
 * Splitting a route into runs when its stops hold more than the courier carries, ported from the phone's
 * lib/services/routing/runs.dart (docs/design/split-runs.html; the user's calls, 2026-10-14): room is counted in small
 * parcels (Large counts 2, Bulky 3); what a courier carries starts from their vehicle; runs are grouped by direction from
 * where they load, each a loop from there; Routr offers a split and never splits by itself. Pure, tested against the
 * phone's own cases (tests/runs.test.ts).
 */

/** What a courier carries, in small parcels, before they set their own (`Runs.defaultCapacity`). */
export function defaultCapacity(vehicle?: string | null): number {
  switch (vehicle) {
    case 'bicycle':
      return 6;
    case 'car':
      return 30;
    case 'tricycle':
      return 40;
    case 'van':
      return 120;
    default:
      return 15;
  }
}

/** Between runs: back at the load point, loading the next one (s). */
export const DEFAULT_LOAD_S = 10 * 60;

/** Small parcels' worth of room per package (ParcelSize.weight). */
export const SIZE_WEIGHT: Record<string, number> = { regular: 1, large: 2, bulky: 3 };

export interface Loadable {
  packageCount?: number | null;
  size?: string | null;
}

/** The room a stop's packages take, in small parcels. Pickups count too: what's picked up rides along. */
export const loadOf = (s: Loadable) => Math.max(1, s.packageCount ?? 1) * (SIZE_WEIGHT[s.size ?? 'regular'] ?? 1);

export const loadOfAll = (stops: Loadable[]) => stops.reduce((a, s) => a + loadOf(s), 0);

/** Packages to load for [stops] (what a courier counts at the shop). */
export const packagesOf = (stops: Loadable[]) => stops.reduce((a, s) => a + Math.max(1, s.packageCount ?? 1), 0);

// ── Splitting (Runs.split) ───────────────────────────────────────────────

export interface Spot {
  lat: number;
  lng: number;
}

/** A stop as runs see it: the phone's DeliveryStop fields that matter here. */
export interface RunStop extends Spot, Loadable {
  id: string;
  /** First, Auto or Last (OrderHint). */
  orderHint?: string | null;
  /** The run the courier put it in by hand (1-based). */
  runHint?: number | null;
  /** Deliver by, as stored (an ISO time) or in ms: earlier runs go first. */
  deliverBy?: string | number | null;
  pickupId?: string | null;
}

/** A linked delivery rides in its pickup's run; when only the delivery was put in a run by hand, its pickup joins it. */
function pairsTogether<T extends RunStop>(runs: T[][]) {
  const runOf = (id: string) => runs.findIndex((r) => r.some((s) => s.id === id));
  for (const d of runs.flat()) {
    const pickupId = d.pickupId;
    if (!pickupId) continue;
    const from = runOf(pickupId);
    const to = runOf(d.id);
    if (from < 0 || to < 0 || from === to) continue;
    const pickup = runs[from].find((s) => s.id === pickupId)!;
    if (d.runHint != null && pickup.runHint == null) {
      runs[from].splice(runs[from].indexOf(pickup), 1);
      runs[to].unshift(pickup);
    } else {
      runs[to].splice(runs[to].indexOf(d), 1);
      runs[from].push(d);
    }
  }
}

/** [stops] round [depot] by compass direction, starting just after the widest empty gap so no run straddles it. */
function bySweep<T extends RunStop>(stops: T[], depot: Spot): T[] {
  if (stops.length < 2) return [...stops];
  const angle = (s: Spot) => Math.atan2(s.lat - depot.lat, (s.lng - depot.lng) * Math.cos((depot.lat * Math.PI) / 180));
  const sorted = [...stops].sort((a, b) => angle(a) - angle(b));
  let widest = -1;
  let startAt = 0;
  for (let i = 0; i < sorted.length; i++) {
    let gap = angle(sorted[i]) - angle(sorted[(i - 1 + sorted.length) % sorted.length]);
    // Only the step from the last back round to the first wraps.
    if (i === 0) gap += 2 * Math.PI;
    if (gap > widest) {
      widest = gap;
      startAt = i;
    }
  }
  return [...sorted.slice(startAt), ...sorted.slice(0, startAt)];
}

/**
 * Splits [stops] into runs from [depot], where the courier loads (`Runs.split`). Without [runs], each run is filled up to
 * [capacity] going round the load point by direction, so no run crosses town twice. With [runs] (a route that would fit,
 * split anyway), the stops are shared out about evenly. Stops put in a run by hand stay there; First pins go in the first
 * run and Last pins in the last. Without hints, runs with an earlier deliver-by go first. A linked delivery rides in its
 * pickup's run. A run may end up over what the courier carries; that's said where it shows.
 */
export function split<T extends RunStop>(stops: T[], depot: Spot, capacity: number, runs?: number | null): T[][] {
  if (!stops.length) return [];
  const cap = Math.max(1, capacity);
  const total = loadOfAll(stops);
  const wanted = Math.max(1, runs ?? Math.ceil(total / cap));
  const hinted = stops.some((s) => s.runHint != null);
  // Where each stop must go, if anywhere (0-based; -1 = the last run).
  const forcedRun = (s: T): number | null => (s.runHint != null ? Math.max(0, s.runHint - 1) : s.orderHint === 'first' ? 0 : s.orderHint === 'last' ? -1 : null);
  const free = stops.filter((s) => forcedRun(s) == null);
  const sweep = bySweep(free, depot);
  const target = runs != null ? Math.max(1, Math.ceil(total / wanted)) : cap;

  // Fill runs in sweep order; leave room for what's forced into them.
  const forcedLoad = new Map<number, number>();
  for (const s of stops) {
    const f = forcedRun(s);
    if (f == null) continue;
    const at = f < 0 ? wanted - 1 : f;
    forcedLoad.set(at, (forcedLoad.get(at) ?? 0) + loadOf(s));
  }
  const out: T[][] = [[]];
  let load = forcedLoad.get(0) ?? 0;
  let full = false;
  const nextRun = () => {
    out.push([]);
    load = forcedLoad.get(out.length - 1) ?? 0;
    full = false;
  };
  for (const s of sweep) {
    if (runs == null) {
      // Up to what the courier carries, never over it.
      if (out.at(-1)!.length && load + loadOf(s) > target) nextRun();
    } else if (full) nextRun();
    out.at(-1)!.push(s);
    load += loadOf(s);
    if (runs != null && load >= target && out.length < wanted) full = true;
  }
  // Forced stops into their runs (a run may then be over).
  const lastIndex = Math.max(out.length, wanted) - 1;
  while (out.length <= lastIndex) out.push([]);
  for (const s of stops) {
    const f = forcedRun(s);
    if (f == null) continue;
    const at = f < 0 ? lastIndex : f;
    while (out.length <= at) out.push([]);
    if (f === 0 && s.orderHint === 'first') out[at].unshift(s);
    else out[at].push(s);
  }
  pairsTogether(out);
  const result = out.filter((r) => r.length);
  if (hinted) return result;
  // Earlier deadlines go first; the run with First pins stays first and the one with Last pins stays last.
  const rank = (run: T[]) => (run.some((s) => s.orderHint === 'first') ? 0 : run.some((s) => s.orderHint === 'last') ? 2 : 1);
  const time = (v: string | number | null | undefined) => (v == null ? null : typeof v === 'number' ? v : Date.parse(v));
  const earliest = (run: T[]) => run.reduce<number | null>((t, s) => {
    const d = time(s.deliverBy);
    return d != null && !Number.isNaN(d) && (t == null || d < t) ? d : t;
  }, null);
  const order = result.map((_, i) => i);
  order.sort((a, b) => {
    const byRank = rank(result[a]) - rank(result[b]);
    if (byRank) return byRank;
    const ea = earliest(result[a]);
    const eb = earliest(result[b]);
    if (ea != null && eb != null && ea !== eb) return ea - eb;
    if (ea != null && eb == null) return -1;
    if (ea == null && eb != null) return 1;
    return a - b;
  });
  return order.map((i) => result[i]);
}

const R = 6371000;
const toRad = (d: number) => (d * Math.PI) / 180;
/** Straight-line metres (HaversineOptimizer.distanceMeters). */
export function metresBetween(a: Spot, b: Spot): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
/** About how long a straight line takes to drive (HaversineOptimizer.straightSeconds: 30 km/h). */
const straightSeconds = (a: Spot, b: Spot) => Math.round(metresBetween(a, b) / ((30 * 1000) / 3600));

const nearestTo = <T extends Spot>(run: T[], to: Spot) => run.reduce((best, s) => (metresBetween(s, to) < metresBetween(best, to) ? s : best), run[0]);

/**
 * About how much longer [runs] take than one run (`Runs.extraTime`): the trips back to load, the loading, and the trips
 * out again, by straight lines (the real figure comes once each run is sorted). In seconds.
 */
export function extraTime(runs: Spot[][], depot: Spot, loadS = DEFAULT_LOAD_S): number {
  if (runs.length < 2) return 0;
  let seconds = 0;
  for (let i = 0; i < runs.length - 1; i++) {
    const back = nearestTo(runs[i], depot);
    const out = nearestTo(runs[i + 1], depot);
    seconds += straightSeconds(back, depot) + straightSeconds(depot, out) - straightSeconds(back, out) + loadS;
  }
  return Math.max(0, seconds);
}

// ── One route out of the runs (Runs.combine) ─────────────────────────────

/** A run's own result, planned from the load point (the shape of routing.ts `Timed`). */
export interface RunPart {
  order: string[];
  legS: number[];
  distanceM: number;
  /** Driving, the way back included when it has one. */
  drivingS: number;
  polyline: string | null;
  method: 'routesApi' | 'haversine';
  /** The way back to where the courier loads; none for the last run without Back to start. */
  backS?: number;
  why?: string;
}

/** A run as the record keeps it (`RunSpan.toJson`). */
export interface RunSpanJson {
  start: number;
  count: number;
  distance_m: number;
  driving_s: number;
  polyline?: string;
  return_s?: number;
  load_s?: number;
}

/**
 * One route out of each run's own result ([parts], in run order, each planned from the load point): each later run's first
 * leg becomes the drive back, the loading and the drive out, so every arrival time includes them (`Runs.combine`). Written
 * as the phone's OptimizationResult: `runs` and `load_at` are what the courier's app reads to run it in runs.
 */
export function combine(parts: RunPart[], depot: Spot, loadS = DEFAULT_LOAD_S, encodeJoin?: (lines: string[]) => string): { order: string[]; legS: number[]; distanceM: number; drivingS: number; polyline: string | null; method: 'routesApi' | 'haversine'; runs: RunSpanJson[]; backS?: number; why?: string } {
  const order: string[] = [];
  const legS: number[] = [];
  const runs: RunSpanJson[] = [];
  let distanceM = 0;
  let drivingS = 0;
  parts.forEach((p, i) => {
    p.legS.forEach((leg, j) => legS.push(Math.round(j === 0 && i > 0 ? leg + (parts[i - 1].backS ?? 0) + loadS : leg)));
    runs.push({
      start: order.length,
      count: p.order.length,
      distance_m: Math.round(p.distanceM),
      driving_s: Math.round(p.drivingS),
      ...(p.polyline ? { polyline: p.polyline } : {}),
      ...(p.backS != null ? { return_s: Math.round(p.backS) } : {}),
      ...(i > 0 ? { load_s: loadS } : {}),
    });
    order.push(...p.order);
    distanceM += p.distanceM;
    drivingS += p.drivingS;
  });
  const lines = parts.map((p) => p.polyline);
  const why = parts.find((p) => p.method === 'haversine')?.why;
  void depot;
  return {
    order,
    legS,
    distanceM,
    // Driving, plus loading between runs: what the time estimates add the time at stops to.
    drivingS: drivingS + loadS * (parts.length - 1),
    polyline: encodeJoin && lines.every((l) => !!l) ? encodeJoin(lines as string[]) : null,
    method: parts.every((p) => p.method === 'routesApi') ? 'routesApi' : 'haversine',
    runs,
    ...(parts.at(-1)?.backS != null ? { backS: parts.at(-1)!.backS } : {}),
    ...(why ? { why } : {}),
  };
}

/** The load list for a run: what's delivered last goes in first, so the first drop is on top (the phone's load list). */
export const loadList = <T extends RunStop>(run: T[]) => [...run].reverse();
